import type { NotificationPriority } from '@/services/notification.service';
import { cn } from '@/constants/design-tokens';

const TONE: Record<NotificationPriority, { dot: string; bg: string; fg: string; ring: string; pulse?: boolean }> = {
  LOW: {
    dot: 'bg-neutral-400 dark:bg-neutral-500',
    bg: 'bg-neutral-100/80 dark:bg-neutral-800/70',
    fg: 'text-neutral-700 dark:text-neutral-200',
    ring: 'ring-1 ring-inset ring-neutral-200 dark:ring-neutral-700/60',
  },
  MEDIUM: {
    dot: 'bg-sky-500',
    bg: 'bg-sky-50 dark:bg-sky-900/25',
    fg: 'text-sky-700 dark:text-sky-200',
    ring: 'ring-1 ring-inset ring-sky-200/70 dark:ring-sky-700/40',
  },
  HIGH: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/25',
    fg: 'text-amber-800 dark:text-amber-200',
    ring: 'ring-1 ring-inset ring-amber-200/70 dark:ring-amber-700/40',
  },
  URGENT: {
    dot: 'bg-rose-500',
    bg: 'bg-rose-50 dark:bg-rose-900/30',
    fg: 'text-rose-700 dark:text-rose-200',
    ring: 'ring-1 ring-inset ring-rose-200/70 dark:ring-rose-700/40',
    pulse: true,
  },
};

export function BroadcastPriorityBadge({ priority }: { priority: NotificationPriority }) {
  const t = TONE[priority];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase tabular-nums',
        t.bg,
        t.fg,
        t.ring,
      )}
    >
      <span className="relative flex h-2 w-2">
        {t.pulse && (
          <span className={cn('animate-ping absolute inline-flex h-full w-full rounded-full opacity-75', t.dot)} />
        )}
        <span className={cn('relative inline-flex h-2 w-2 rounded-full', t.dot)} />
      </span>
      {priority}
    </span>
  );
}
